import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import { useUrlState } from "@/lib/useUrlState";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RISK_META, RISK_FALLBACK, formatLastSeen, RISK_LEVELS } from "@/lib/risk";
import { RiskBadge, TierBadge, PocChip, PocChipPair, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import RulesInfoDialog from "@/components/RulesInfoDialog";
import ExportButton from "@/components/ExportButton";
import { FIRM_BASE_COLUMNS } from "@/lib/exportColumns";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Buildings, MagnifyingGlass, CaretRight,
} from "@phosphor-icons/react";
import FirmRowActions from "@/components/FirmRowActions";

const RISK_DOT = {
  Active: "bg-emerald-500",
  "At Risk": "bg-amber-500",
  Inactive: "bg-orange-500",
  "Potential Churn": "bg-red-500",
};

export default function OldCustomers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPoc = user?.role === "poc";

  const [search, setSearch] = useUrlState("search", "");
  const [tier, setTier] = useUrlState("tier", "all");
  const [poc, setPoc] = useUrlState("poc", "all");
  const [risk, setRisk] = useUrlState("risk", "all");
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [pocOptions, setPocOptions] = useState([]);
  const [tierOptions, setTierOptions] = useState([]);

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setPocOptions(r.data.pocs);
      setTierOptions(r.data.tiers);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/customers/old", {
        params: {
          search: search.trim() || undefined,
          tier: tier !== "all" ? tier : undefined,
          poc: poc !== "all" ? poc : undefined,
          risk: risk !== "all" ? risk : undefined,
        },
      })
      .then((r) => {
        setCustomers(r.data.customers);
        setCounts(r.data.counts);
      })
      .finally(() => setLoading(false));
  }, [search, tier, poc, risk]);

  const totalOld = RISK_LEVELS.reduce((s, r) => s + (counts[r] || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
          <Buildings size={28} weight="fill" className="text-slate-700" />
          Old Customers
        </h1>
        <p className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
          <span>Live firms with tenure greater than 12 months. Enterprise firms appear here too — their badge uses Enterprise rules.</span>
          <RulesInfoDialog view="old_customers" label="Old Customers" />
        </p>
      </div>

      {/* Risk cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="oc-risk-cards">
        <RiskCard
          label="All Old"
          value={totalOld}
          dotClass="bg-gray-400"
          active={risk === "all"}
          onClick={() => setRisk("all")}
          testid="oc-card-all"
        />
        {RISK_LEVELS.map((r) => (
          <RiskCard
            key={r}
            label={r}
            value={counts[r] || 0}
            dotClass={RISK_DOT[r]}
            active={risk === r}
            onClick={() => setRisk(risk === r ? "all" : r)}
            testid={`oc-card-${r.replace(/\s+/g, "-")}`}
          />
        ))}
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap gap-2 items-center" data-testid="oc-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search firm, subdomain, city, POC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="oc-search"
          />
        </div>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-[150px]" data-testid="oc-filter-tier">
            <SelectValue placeholder="All tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={poc} onValueChange={setPoc}>
          <SelectTrigger className="w-[160px]" data-testid="oc-filter-poc">
            <SelectValue placeholder="All POCs" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={risk} onValueChange={setRisk}>
          <SelectTrigger className="w-[180px]" data-testid="oc-filter-risk">
            <SelectValue placeholder="All risk" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All risk</SelectItem>
            {RISK_LEVELS.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-xs text-gray-500 ml-auto">
          {loading ? "Loading…" : `${customers.length} firm${customers.length === 1 ? "" : "s"}`}
        </div>
        <ExportButton
          filename={`old-customers-${risk === "all" ? "all" : risk.replace(/\s+/g, "-").toLowerCase()}`}
          sheetName="Old Customers"
          columns={FIRM_BASE_COLUMNS}
          rows={customers}
          testId="export-old-customers"
        />
      </div>

      {/* List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="oc-list">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-left">
              <Th>Firm</Th>
              <Th>Users</Th>
              <Th>Tier</Th>
              <Th>Risk</Th>
              <Th className="whitespace-nowrap min-w-[140px]">Last seen</Th>
              <Th>POC</Th>
              <th className="w-16"></th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">Loading…</td></tr>
            )}
            {!loading && customers.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">No old customers match the current filters.</td></tr>
            )}
            {!loading && customers.map((c) => {
              const meta = RISK_META[c.risk] || RISK_FALLBACK;
              return (
                <tr
                  key={c.subdomain}
                  onClick={() => navigate(`/firms/${c.subdomain}${firmFromQS(location)}`)}
                  className={`border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${meta.leftClass}`}
                  data-testid={`oc-row-${c.subdomain}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.firm_name}</div>
                    <FirmSubmeta firm={c} />
                  </td>
                  <td className="px-4 py-3"><UsersCell value={c.num_users} /></td>
                  <td className="px-4 py-3"><TierBadge tier={c.tier} /></td>
                  <td className="px-4 py-3 whitespace-nowrap"><RiskBadge risk={c.risk} /></td>
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                    {formatLastSeen(c.last_seen, c.days_since_seen)}
                  </td>
                  <td className="px-4 py-3"><PocChipPair poc={c.poc} salesPoc={c.sales_poc} /></td>
                  <td className="px-2 py-3 whitespace-nowrap"><FirmRowActions firm={c} /></td>
                  <td className="px-4 py-3 text-right"><CaretRight size={16} className="text-gray-400" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RiskCard({ label, value, dotClass, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`bg-white border rounded-lg p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm ${
        active ? "border-gray-900 ring-2 ring-gray-900/10" : "border-gray-200"
      }`}
      data-testid={testid}
      aria-pressed={active}
    >
      <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 font-medium flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
        {label}
      </div>
      <div className="mt-2 font-display text-[28px] leading-none font-semibold text-gray-900 tabular-nums">
        {value}
      </div>
    </button>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-medium text-gray-500 ${className}`}>
      {children}
    </th>
  );
}
